import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useAdminTimeseries,
  useBackfillTimeseries,
  type MetricsSnapshot,
  type SnapshotStat,
} from "@/features/admin";
import { useState } from "react";
import { toast } from "sonner";
import { AdminPageLayout } from "../layout";
import { StatCard } from "./components/stat-card";
import {
  MAX_SERIES,
  TimeseriesChart,
  type TimeseriesSeries,
} from "./components/timeseries-chart";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const ms = (n: number) =>
  n >= 1000 ? `${(n / 1000).toFixed(2)} s` : `${Math.round(n)} ms`;

const WINDOWS = [
  { hours: 24, label: "24 dernières heures" },
  { hours: 24 * 3, label: "3 derniers jours" },
  { hours: 24 * 7, label: "7 derniers jours" },
  { hours: 24 * 30, label: "30 derniers jours" },
];

type Metric = "p50" | "p75" | "p95";

const METRICS: { value: Metric; label: string }[] = [
  { value: "p50", label: "p50 (médiane)" },
  { value: "p75", label: "p75" },
  { value: "p95", label: "p95 (pics)" },
];

/** Median of a sample list. Used on the hourly values, i.e. "a typical hour". */
const median = (values: number[]): number => {
  if (!values.length) return 0;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
};

/** Hourly value of a stat, or null when the hour holds no measurement (leaves a gap). */
const pointOf = (stat: SnapshotStat | undefined, metric: Metric) =>
  stat && stat.count > 0 ? stat[metric] : null;

/**
 * Per-model stability over the window. `spike` (worst hour ÷ typical hour) is the number to
 * read: a provider can have a decent overall p95 and still double its latency for two hours
 * a day — that is exactly what a pooled 30-day dashboard hides.
 */
interface ModelStability {
  key: string;
  provider: string;
  model: string;
  turns: number;
  hours: number;
  typicalP50: number;
  typicalP95: number;
  worstP95: number;
  worstAt: number;
  spike: number;
  degradedHours: number;
}

const stabilityOf = (snapshots: MetricsSnapshot[]): ModelStability[] => {
  const keys = new Map<string, { provider: string; model: string }>();
  for (const s of snapshots) {
    for (const [key, stat] of Object.entries(s.llm_by_model || {})) {
      if (stat.count > 0 && !keys.has(key)) {
        keys.set(key, { provider: stat.provider, model: stat.model });
      }
    }
  }

  return [...keys.entries()]
    .map(([key, { provider, model }]) => {
      const hours = snapshots
        .map((s) => ({ at: s.bucket_start, stat: s.llm_by_model?.[key] }))
        .filter((h) => !!h.stat?.count)
        .map((h) => ({ at: h.at, stat: h.stat! }));
      const stats = hours.map((h) => h.stat);

      const typicalP50 = median(stats.map((s) => s.p50));
      const typicalP95 = median(stats.map((s) => s.p95));
      const worst = hours.reduce(
        (acc, h) => (h.stat.p95 > acc.value ? { value: h.stat.p95, at: h.at } : acc),
        { value: 0, at: 0 },
      );

      return {
        key,
        provider,
        model,
        turns: stats.reduce((n, s) => n + s.count, 0),
        hours: hours.length,
        typicalP50,
        typicalP95,
        worstP95: worst.value,
        worstAt: worst.at,
        spike: typicalP95 > 0 ? worst.value / typicalP95 : 0,
        // "Dégradée" = an hour whose p95 is at least twice the typical hour's.
        degradedHours: stats.filter((s) => typicalP95 > 0 && s.p95 >= 2 * typicalP95)
          .length,
      };
    })
    .sort((a, b) => b.turns - a.turns);
};

/** Colour a spike ratio: stable under 1.5×, watch under 2.5×, erratic beyond. */
const spikeTone = (r: number): "default" | "warn" | "bad" =>
  r >= 2.5 ? "bad" : r >= 1.5 ? "warn" : "default";

/**
 * Temporal view of the technical metrics: the same p50/p95 as the technical dashboard, but
 * hour by hour, from the snapshots written by the backend cron. Answers "is this provider
 * consistently fast, or fast on average and occasionally awful?".
 */
export function AdminStabilityPage() {
  const [hours, setHours] = useState(24 * 7);
  const [metric, setMetric] = useState<Metric>("p95");
  const { data: snapshots = [], isLoading } = useAdminTimeseries(hours);
  const backfill = useBackfillTimeseries();

  const stability = stabilityOf(snapshots);
  const topModels = stability.slice(0, MAX_SERIES);

  const llmSeries: TimeseriesSeries[] = topModels.map((m, i) => ({
    id: m.key,
    colorIndex: i,
    data: snapshots.map((s) => ({
      x: s.bucket_start,
      y: pointOf(s.llm_by_model?.[m.key], metric),
    })),
  }));

  const delaySeries: TimeseriesSeries[] = [
    {
      id: "Délai de réponse",
      colorIndex: 0,
      data: snapshots.map((s) => ({
        x: s.bucket_start,
        y: pointOf(s.response_delay, metric),
      })),
    },
    {
      id: "LLM — 1er token (étage)",
      colorIndex: 1,
      data: snapshots.map((s) => ({
        x: s.bucket_start,
        y: pointOf(s.latency_stages?.llm_first_token_ms, metric),
      })),
    },
    {
      id: "Audio — 1er chunk",
      colorIndex: 2,
      data: snapshots.map((s) => ({
        x: s.bucket_start,
        y: pointOf(s.latency_stages?.audio_first_chunk_ms, metric),
      })),
    },
  ];

  const ttsProviders = [
    ...new Set(
      snapshots.flatMap((s) =>
        Object.entries(s.tts_by_provider || {})
          .filter(([, stat]) => stat.count > 0)
          .map(([key]) => key),
      ),
    ),
  ].slice(0, MAX_SERIES);

  const ttsSeries: TimeseriesSeries[] = ttsProviders.map((provider, i) => ({
    id: provider,
    colorIndex: i,
    data: snapshots.map((s) => ({
      x: s.bucket_start,
      y: pointOf(s.tts_by_provider?.[provider], metric),
    })),
  }));

  const volumeSeries: TimeseriesSeries[] = [
    {
      id: "Tours mesurés",
      colorIndex: 4,
      data: snapshots.map((s) => ({ x: s.bucket_start, y: s.turns })),
    },
  ];

  const measuredHours = snapshots.filter((s) => s.turns > 0).length;
  const truncated = snapshots.some((s) => s.truncated);

  const handleBackfill = async () => {
    try {
      const res = await backfill.mutateAsync(hours);
      toast.success(`${res.buckets} heure(s) recalculée(s)`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Recalcul impossible",
      );
    }
  };

  return (
    <AdminPageLayout
      title="Stabilité dans le temps"
      description="Latences heure par heure, tous tenants confondus — pour distinguer « lent » de « irrégulier »"
      actions={[
        <div className="flex items-center gap-2" key="actions">
          <Select
            value={metric}
            onValueChange={(v) => setMetric(v as Metric)}
          >
            <SelectTrigger className="w-[170px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {METRICS.map((m) => (
                <SelectItem key={m.value} value={m.value}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={String(hours)}
            onValueChange={(v) => setHours(Number(v))}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WINDOWS.map((w) => (
                <SelectItem key={w.hours} value={String(w.hours)}>
                  {w.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={handleBackfill}
            disabled={backfill.isPending}
          >
            {backfill.isPending ? "Recalcul…" : "Recalculer"}
          </Button>
        </div>,
      ]}
    >

      <p className="text-xs text-muted-foreground">
        Un instantané par heure est calculé automatiquement à partir des métriques
        de chaque tour de parole. Les heures sans appel restent vides (le trait
        est coupé) au lieu de retomber à zéro. « Recalculer » reconstruit la
        fenêtre affichée — utile la première fois, pour remonter l'historique
        déjà présent en base.
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Heures avec trafic"
          value={measuredHours}
          hint={`${snapshots.length} heure(s) dans la fenêtre`}
          isLoading={isLoading}
        />
        <StatCard
          title="Tours mesurés"
          value={snapshots
            .reduce((n, s) => n + s.turns, 0)
            .toLocaleString("fr-FR")}
          isLoading={isLoading}
        />
        <StatCard
          title="Modèles observés"
          value={stability.length}
          isLoading={isLoading}
        />
        <StatCard
          title="Pire écart LLM"
          value={
            topModels.length
              ? `${Math.max(...topModels.map((m) => m.spike)).toFixed(1)} ×`
              : "—"
          }
          hint="pire heure ÷ heure typique (p95)"
          tone={
            topModels.length
              ? spikeTone(Math.max(...topModels.map((m) => m.spike)))
              : "default"
          }
          isLoading={isLoading}
        />
      </div>

      {truncated ? (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          Certaines heures ont dépassé la limite d'agrégation : leurs valeurs ne
          portent que sur le début de l'heure.
        </p>
      ) : null}

      <TimeseriesChart
        title={`Latence LLM (TTFT pur) — ${metric}, par modèle`}
        description="Temps réel du provider jusqu'au premier token, hors préparation interne. C'est la courbe à regarder pour juger la régularité d'un fournisseur."
        series={llmSeries}
        format={ms}
        isLoading={isLoading}
        height={320}
      />

      <StabilityTable rows={stability} isLoading={isLoading} />

      <TimeseriesChart
        title={`Délai de réponse et ses étages — ${metric}`}
        description="Bout en bout : fin de parole du client → premier chunk audio, et la décomposition par étage."
        series={delaySeries}
        format={ms}
        isLoading={isLoading}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TimeseriesChart
          title={`Latence TTS — ${metric}, par provider`}
          description="Premier chunk audio après le premier token LLM."
          series={ttsSeries}
          format={ms}
          isLoading={isLoading}
          height={240}
        />
        <TimeseriesChart
          title="Volume — tours mesurés par heure"
          description="Contexte de lecture : un pic de latence sur 3 tours ne vaut pas un pic sur 300."
          series={volumeSeries}
          format={(v) => Math.round(v).toLocaleString("fr-FR")}
          isLoading={isLoading}
          height={240}
        />
      </div>
    </AdminPageLayout>
  );
}

function StabilityTable({
  rows,
  isLoading,
}: {
  rows: ModelStability[];
  isLoading?: boolean;
}) {
  const when = (t: number) =>
    t
      ? new Date(t).toLocaleString("fr-FR", {
          day: "2-digit",
          month: "2-digit",
          hour: "2-digit",
        })
      : "—";

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Régularité par modèle</CardTitle>
        <p className="text-xs text-muted-foreground">
          « Heure typique » = médiane des heures de la fenêtre. « Écart » = pire
          heure ÷ heure typique (p95) : au-delà de 2×, le modèle a vraiment
          ramé à un moment. « Heures dégradées » compte les heures à plus du
          double de l'heure typique.
        </p>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-48 w-full rounded-lg" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucune donnée.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Modèle</TableHead>
                <TableHead className="text-right">Tours</TableHead>
                <TableHead className="text-right">Heures</TableHead>
                <TableHead className="text-right">p50 typique</TableHead>
                <TableHead className="text-right">p95 typique</TableHead>
                <TableHead className="text-right">Pire heure</TableHead>
                <TableHead className="text-right">Écart</TableHead>
                <TableHead className="text-right">Heures dégradées</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.key}>
                  <TableCell>
                    <span className="font-medium">{r.provider}</span>
                    <span className="text-muted-foreground"> · {r.model}</span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {r.turns.toLocaleString("fr-FR")}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {r.hours}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {ms(r.typicalP50)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {ms(r.typicalP95)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {ms(r.worstP95)}
                    <span className="text-muted-foreground">
                      {" "}
                      ({when(r.worstAt)})
                    </span>
                  </TableCell>
                  <TableCell
                    className={`text-right tabular-nums font-medium ${
                      spikeTone(r.spike) === "bad"
                        ? "text-red-600 dark:text-red-400"
                        : spikeTone(r.spike) === "warn"
                          ? "text-amber-600 dark:text-amber-400"
                          : ""
                    }`}
                  >
                    {r.spike ? `${r.spike.toFixed(1)} ×` : "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {r.degradedHours}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
